/**
 * Shared copy for non–windscreen claim describe steps (incident + damage fields).
 * @module claims/claimDescribeCopy
 */

/** Max characters per incident / damage textarea on describe steps. */
export const CLAIM_DESCRIBE_MAX_FIELD_LENGTH = 1000

/**
 * Intro under "What happened" on describe steps for single-vehicle, weather, fire, theft,
 * malicious damage, and other loss (not windscreen, multi-vehicle, or off-road).
 */
export const OTHER_BRANCH_WHAT_HAPPENED_INTRO =
  'Describe how the incident occurred and the conditions around it. The more detail you share, the easier it is for us to understand what happened. You do not need to use full sentences. Dot points are fine.'

/** Incident prompts for weather, fire, theft, and single-vehicle describe steps. */
export const OTHER_BRANCH_INCIDENT_PROMPTS = [
  'Where you were going to & from.',
  'The direction you were traveling.',
  'Traffic and/or road conditions.',
  'Conditions such as the weather.',
  'Events leading up to the incident.',
] as const

/** Incident prompts when the vehicle was parked at the time of the incident. */
export const OTHER_BRANCH_PARKED_INCIDENT_PROMPTS = [
  'Where the vehicle was parked (e.g. driveway, street, car park, workplace).',
  'When you last saw the vehicle before the incident, and when you discovered what happened.',
  'Traffic and/or road conditions.',
  'Conditions such as the weather.',
  'Events leading up to the incident.',
] as const

/**
 * Returns incident prompt bullets for the describe step based on whether the vehicle was parked.
 */
export function incidentPromptsForVehicleParked(
  vehicleParkedAtIncident: boolean | null | undefined
): readonly string[] {
  return vehicleParkedAtIncident === true
    ? OTHER_BRANCH_PARKED_INCIDENT_PROMPTS
    : OTHER_BRANCH_INCIDENT_PROMPTS
}

/** Damage prompts shared with multi-vehicle collision describe. */
export const MULTI_VEHICLE_DAMAGE_PROMPTS = [
  'Damage to your vehicle (areas of the vehicle and what you can see)',
  'Contact with other vehicles or objects (what hit what, if clear)',
  'Whether vehicles were driveable or needed a tow',
  'Other property damaged (e.g. sign, fence), if applicable',
] as const

/** Intro copy above the damage description field on describe steps. */
export const CLAIM_DAMAGE_SECTION_INTRO =
  'Describe damage and impact to your vehicle, others involved, and any other property. If you are unsure of technical terms, plain language is fine.'
